'use client';

import { useAuth0 } from '@auth0/auth0-react';
import { useEffect } from 'react';

import { isLastActiveExpired, runThrottledAuth0ClaimsRefresh } from '@libs/core';

import { type Auth0IdTokenClaims, hasAuth0LoyaltyId, logSentryError } from '../../utils';
import { auth0TokenSyncSentryTags } from './auth0TokenSyncSentryTags';
import { isAuth0CallbackPath } from './pendingAuth0ReturnTo';

const LOYALTY_CLAIM_SYNC_SENTRY = {
    component: 'useAuth0LoyaltyClaimSync',
    action: 'runThrottledAuth0ClaimsRefresh',
} as const;

/**
 * Longest delay this hook waits before leaving the next attempt to a page load.
 * The coordinator back-off tops out at 60s, so every step runs in-tab; this ceiling
 * only matters if the schedule grows again.
 */
const MAX_IN_TAB_RETRY_DELAY_MS = 5 * 60 * 1000;

/**
 * Re-mints the Auth0 tokens while the session's Treats Rewards id is still `'pending'`.
 *
 * Loyalty enrollment can land in `app_metadata` minutes after login, and nothing pushes that
 * to the browser: the id token keeps the claim it was issued with, and a reload replays it
 * from the SDK cache, so loyalty stays unavailable until the shopper signs in again (CIAM-816).
 *
 * Reads the claims rather than `LoyaltyProvider` state so the token stays the single source
 * of truth. Attempts are throttled and coordinated across tabs by
 * `runThrottledAuth0ClaimsRefresh`; this hook only decides when to ask, retrying on the
 * back-off it reports while the tab is visible.
 */
export function useAuth0LoyaltyClaimSync() {
    const { getIdTokenClaims, isAuthenticated, isLoading } = useAuth0();

    useEffect(() => {
        if (isLoading || !isAuthenticated) {
            return;
        }

        // The callback route document-navigates once the cart merge finishes, which can abort
        // an `/oauth/token` call Auth0 has already answered with a rotated refresh token. The
        // destination page would then present the superseded one and reuse detection would
        // revoke the grant. That page remints on load, so nothing is lost by waiting.
        if (isAuth0CallbackPath(globalThis.window.location.pathname)) {
            return;
        }

        // Guards against a retry scheduled before an auth transition refreshing the new session.
        let isCurrentEffect = true;
        let isAttempting = false;
        let timeoutId: ReturnType<typeof setTimeout> | undefined;

        async function readIdTokenClaims(): Promise<Auth0IdTokenClaims | undefined> {
            return (await getIdTokenClaims()) as Auth0IdTokenClaims | undefined;
        }

        function shouldSkipPreflight(): boolean {
            return isCurrentEffect === false || isAttempting || isLastActiveExpired();
        }

        function scheduleRetry(retryAfterMs: number | undefined) {
            if (!isCurrentEffect || retryAfterMs === undefined || retryAfterMs > MAX_IN_TAB_RETRY_DELAY_MS) {
                return;
            }

            // A visibility change can race a pending timer; keep a single retry outstanding.
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                void refreshStaleLoyaltyClaim();
            }, retryAfterMs);
        }

        async function refreshStaleLoyaltyClaim(): Promise<void> {
            if (shouldSkipPreflight()) {
                return;
            }

            isAttempting = true;
            try {
                const claims = await readIdTokenClaims();
                const subject = claims?.sub;

                if (isCurrentEffect === false || subject === undefined || subject.length === 0) {
                    return;
                }
                if (hasAuth0LoyaltyId(claims)) {
                    return;
                }

                const result = await runThrottledAuth0ClaimsRefresh({
                    subject,
                    hasClaimResolved: async () => hasAuth0LoyaltyId(await readIdTokenClaims()),
                });

                scheduleRetry(result.retryAfterMs);
            } catch (error) {
                logSentryError(error, auth0TokenSyncSentryTags(LOYALTY_CLAIM_SYNC_SENTRY));
            } finally {
                isAttempting = false;
            }
        }

        // Refreshes are skipped for hidden tabs, so pick the work back up on return.
        const handleVisibilityChange = () => {
            if (globalThis.document.visibilityState === 'visible') {
                void refreshStaleLoyaltyClaim();
            }
        };

        globalThis.document.addEventListener('visibilitychange', handleVisibilityChange);
        void refreshStaleLoyaltyClaim();

        return () => {
            isCurrentEffect = false;
            clearTimeout(timeoutId);
            globalThis.document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [getIdTokenClaims, isAuthenticated, isLoading]);
}
